import { useEffect, useRef } from 'react';
import axios from 'api/request';
import { endPoints } from 'api/endpoints';
import { useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { sanitizeCapabilityName, getGithubOrgUnit } from 'pages/provisioning/day2/github-onboarding-new/utils';
import { MIGRATE_EXISTING_OPTION } from 'pages/provisioning/day2/github-onboarding-new/utils/constant';

const useSyncRepoNames = ({ debouncedCapability, repoList, setRepoList, order }) => {
    const isInitialRender = useRef(true);
    const prevCapabilityRef = useRef(null);

    const repoNamePostApi = useMutation((payload) => axios.post(endPoints.day2Operation.GithubOnboarding.convertGithubRepoName, payload));

    const isGithubMigration = order?.isMigratingADO === MIGRATE_EXISTING_OPTION.GITHUB;

    const buildUpdatedRepo = (repo, githubNameResponse) => {
        if (isGithubMigration) {
            return {
                ...repo,
                editableName: repo?.editableName,
                githubEnterpriseName: githubNameResponse?.data,
            };
        }
        return { ...repo, editableName: repo?.editableName, githubName: githubNameResponse?.data };
    };

    const syncRepoNames = async (capability) => {
        const sanitizedCapability = sanitizeCapabilityName(capability);

        try {
            const updated = await Promise.all(
                repoList.map(async (repo) => {
                    try {
                        const githubNameResponse = await repoNamePostApi.mutateAsync({
                            capability: sanitizedCapability,
                            onboardingType: order?.onboardingType,
                            adoRepositoryName: repo?.editableName,
                            organizationUnit: getGithubOrgUnit(order?.environment),
                        });
                        return buildUpdatedRepo(repo, githubNameResponse);
                    } catch (err) {
                        // eslint-disable-next-line no-console
                        console.error('Error regenerating repo name:', err);
                        toast.error('Failed to regenerate repository name. Please try again.');
                        return repo;
                    }
                })
            );
            setRepoList(updated);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Error syncing repo names:', err);
        }
    };

    // Sync on open — if repos already exist but their prefix doesn't match the current capability
    useEffect(() => {
        const hasRepos = Array.isArray(repoList) && repoList.length > 0;
        const canSync = !!debouncedCapability;
        if (!hasRepos || !canSync) return;

        const currentPrefix = sanitizeCapabilityName(debouncedCapability);
        const anyMismatch = repoList.some((repo) => {
            const name = isGithubMigration ? repo.githubEnterpriseName : repo.githubName;
            if (!name) return true;
            return !name.startsWith(currentPrefix);
        });

        if (anyMismatch) {
            syncRepoNames(debouncedCapability);
        }
    }, []); // eslint-disable-line

    // Sync on capability change — re-sync all repo names when capability is modified
    useEffect(() => {
        if (isInitialRender.current) {
            isInitialRender.current = false;
            prevCapabilityRef.current = debouncedCapability;
            return;
        }

        if (!repoList?.length) {
            prevCapabilityRef.current = debouncedCapability;
            return;
        }

        const capabilityChanged = prevCapabilityRef.current !== debouncedCapability;

        if (debouncedCapability && capabilityChanged) {
            syncRepoNames(debouncedCapability);
        }

        prevCapabilityRef.current = debouncedCapability;
    }, [debouncedCapability]); // eslint-disable-line

    return { repoNamePostApi };
};

export default useSyncRepoNames;
